import { useCallback, useEffect, useMemo, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { userIsBuero, type CurrentUser } from "@/lib/auth";
import {
  createAuftragAufmass,
  deleteAuftragAufmass,
  getAuftragAufmass,
  normalizeAufmassFelder,
  updateAuftragAufmass,
  type AufmassSection,
  type AufmassSheet,
  type AufmassValue,
} from "@/lib/aufmass";
import {
  deleteAufmassunterlage,
  fetchAufmassDeckblattPdf,
  fetchMontageunterlageFileBlob,
  getAufmassunterlagen,
  isMailMontageFile,
  resolveMontageunterlageFileUrl,
  type Montageunterlage,
} from "@/lib/projectOverview";
import { MontagePdfEmbed } from "@/projectOverview/MontagePdfEmbed";
import { MailFilePreview } from "@/projectOverview/MailFilePreview";
import { BrandschutzBlattForm } from "@/projectOverview/BrandschutzBlattForm";
import {
  MontageunterlagenAddDialog,
  type MontageAddMode,
} from "@/projectOverview/MontageunterlagenAddDialog";
import { MontageunterlagenPrintDialog } from "@/projectOverview/MontageunterlagenPrintDialog";
import type { MontagePackTab } from "@/projectOverview/montageunterlagenTabs";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { userCanManageMontageunterlagen, userCanViewMontageunterlagen } from "@/projectOverview/permissions";
import { PROJECT_OVERVIEW_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

function fileLabel(item: Montageunterlage): string {
  return item.title || item.filename || "Datei";
}

function isImageFile(item: Montageunterlage): boolean {
  return String(item.content_type || "").toLowerCase().startsWith("image/");
}

function isPdfFile(item: Montageunterlage): boolean {
  const type = String(item.content_type || "").toLowerCase();
  if (type.includes("pdf")) return true;
  return String(item.filename || "").toLowerCase().endsWith(".pdf");
}

function AufmassFilePreview({ orderId, item }: { orderId: number; item: Montageunterlage }) {
  const title = fileLabel(item);
  if (isMailMontageFile(item)) {
    return <MailFilePreview orderId={orderId} itemId={item.id} />;
  }
  if (isPdfFile(item)) {
    return (
      <MontagePdfEmbed
        loadKey={`aufmass-file-${item.id}`}
        loadPdf={() => fetchMontageunterlageFileBlob(orderId, item)}
        title={title}
        paperKey={`aufmass-file-${orderId}-${item.id}`}
      />
    );
  }
  if (isImageFile(item)) {
    return (
      <section className="rounded-xl border border-separator bg-fill-quaternary/15 p-4">
        <p className="mb-3 text-sm font-medium text-label">{title}</p>
        <img
          src={resolveMontageunterlageFileUrl(item, orderId)}
          alt={item.filename || title}
          crossOrigin="use-credentials"
          className="mx-auto max-h-[min(60vh,40rem)] w-auto max-w-full object-contain"
        />
      </section>
    );
  }
  return (
    <section className="flex min-h-[12rem] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-separator px-6 py-10 text-center">
      <p className="text-sm font-medium text-label">{title}</p>
      <a
        href={resolveMontageunterlageFileUrl(item, orderId)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-accent hover:underline"
      >
        Datei öffnen
      </a>
    </section>
  );
}

export function AufmassDialog({
  open,
  orderId,
  user,
  groups,
  initialTabKey = null,
  onClose,
}: {
  open: boolean;
  orderId: number;
  user: CurrentUser | null;
  groups: string[];
  /** Reiter beim Öffnen, z. B. `file-12`, `blatt-3` oder `aufmass-blatt`. */
  initialTabKey?: string | null;
  onClose: () => void;
}) {
  const canFill = userCanViewMontageunterlagen(user, groups);
  const canManage = userCanManageMontageunterlagen(user, groups);
  const isBuero = userIsBuero(user);
  const isHandheld = useMediaQuery("(max-width: 1023px)");
  const [tab, setTab] = useState("deckblatt");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<Montageunterlage[]>([]);
  const [schema, setSchema] = useState<AufmassSection[]>([]);
  const [sheets, setSheets] = useState<AufmassSheet[]>([]);
  const [auftraggeber, setAuftraggeber] = useState<string | null>(null);
  const [auftragsnummer, setAuftragsnummer] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, AufmassValue>>({});
  const [dirty, setDirty] = useState(false);
  const [addMode, setAddMode] = useState<MontageAddMode | null>(null);
  const [editingItem, setEditingItem] = useState<Montageunterlage | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  const activeSheet = sheets.find((sheet) => `blatt-${sheet.id}` === tab) || null;

  const applyState = useCallback((state: Awaited<ReturnType<typeof getAuftragAufmass>>, sheetId?: number) => {
    const nextSchema = state.schema || [];
    const nextSheets = state.sheets || [];
    setSchema(nextSchema);
    setSheets(nextSheets);
    setAuftraggeber(state.auftraggeber || null);
    setAuftragsnummer(state.auftragsnummer || null);
    const current = nextSheets.find((sheet) => sheet.id === sheetId) || null;
    setDraft(current ? normalizeAufmassFelder(nextSchema, current.felder) : {});
    setDirty(false);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [files, form] = await Promise.all([
        getAufmassunterlagen(orderId),
        getAuftragAufmass(orderId),
      ]);
      setItems(Array.isArray(files) ? files : []);
      applyState(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Aufmaßunterlagen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [applyState, orderId]);

  useEffect(() => {
    if (!open) return;
    setTab(initialTabKey || "deckblatt");
    void reload();
  }, [open, reload, initialTabKey]);

  useEffect(() => {
    if (!activeSheet) return;
    if (dirty) return;
    setDraft(normalizeAufmassFelder(schema, activeSheet.felder));
  }, [activeSheet, dirty, schema]);

  const printTabs = useMemo<MontagePackTab[]>(() => {
    const tabs: MontagePackTab[] = [{ key: "deckblatt", kind: "deckblatt", label: "Deckblatt" }];
    for (const sheet of sheets) {
      tabs.push({
        key: `blatt-${sheet.id}`,
        kind: "aufmassblatt",
        label: sheet.titel || "Brandschutztür",
        sheetId: sheet.id,
      });
    }
    for (const item of items.filter((entry) => entry.entry_type === "file")) {
      tabs.push({
        key: `file-${item.id}`,
        kind: "file",
        label: fileLabel(item),
        item,
      });
    }
    return tabs;
  }, [items, sheets]);

  const tabOptions = useMemo(
    () => [
      { value: "deckblatt", label: "Deckblatt" },
      ...sheets.map((sheet) => ({ value: `blatt-${sheet.id}`, label: sheet.titel || "Brandschutztür" })),
      { value: "dateien", label: "Dateien" },
    ],
    [sheets],
  );

  function patchField(fieldId: string, value: AufmassValue) {
    setDraft((current) => ({ ...current, [fieldId]: value }));
    setDirty(true);
  }

  async function handleCreateSheet() {
    setSaving(true);
    setError(null);
    try {
      const state = await createAuftragAufmass(orderId);
      applyState(state, state.created_id);
      if (state.created_id) setTab(`blatt-${state.created_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Brandschutzblatt konnte nicht angelegt werden.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSave(): Promise<boolean> {
    if (!activeSheet) return false;
    setSaving(true);
    setError(null);
    try {
      const state = await updateAuftragAufmass(orderId, activeSheet.id, draft);
      applyState(state, activeSheet.id);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handlePrint() {
    if (dirty && activeSheet) {
      const saved = await handleSave();
      if (!saved) return;
    }
    setPrintOpen(true);
  }

  async function handleDeleteSheet(sheet: AufmassSheet) {
    if (!window.confirm("Aufmaßblatt wirklich löschen?")) return;
    setError(null);
    try {
      const state = await deleteAuftragAufmass(orderId, sheet.id);
      applyState(state);
      setTab("deckblatt");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  async function handleDelete(item: Montageunterlage) {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    setError(null);
    try {
      await deleteAufmassunterlage(orderId, item.id);
      setItems((current) => current.filter((entry) => entry.id !== item.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  const notes = items.filter((item) => item.entry_type === "note" || item.entry_type === "address");
  const files = items.filter((item) => item.entry_type === "file");
  const selectedFile = tab.startsWith("file-")
    ? files.find((item) => item.id === Number(tab.slice("file-".length))) ?? null
    : null;
  const segmentValue = tab.startsWith("file-")
    ? "dateien"
    : tabOptions.some((option) => option.value === tab)
      ? tab
      : "deckblatt";

  useEffect(() => {
    if (!open || loading) return;
    if (tab === "aufmass-blatt") {
      const first = sheets[0];
      setTab(first ? `blatt-${first.id}` : "deckblatt");
      return;
    }
    if (tab.startsWith("file-") && !items.some((item) => item.entry_type === "file" && `file-${item.id}` === tab)) {
      setTab("dateien");
    }
  }, [open, loading, tab, sheets, items]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title="Aufmaßunterlagen"
        description="Blaues Deckblatt und Brandschutztüren-Aufmaß nach dem Vordruck. Maße können digital oder handschriftlich auf dem Ausdruck ergänzt werden."
        size="2xl"
        fullScreenMobile
        zIndexClass={PROJECT_OVERVIEW_OVERLAY_Z}
        dirty={dirty || addMode != null}
        bodyClassName="max-h-[min(85vh,920px)] overflow-y-auto max-sm:max-h-none max-sm:flex-1"
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1 overflow-x-auto">
            <SegmentedControl
              name="Aufmaßbereich"
              value={segmentValue}
              onChange={(next) => {
                if (dirty && next !== tab && !window.confirm("Ungespeicherte Änderungen am Aufmaßblatt verwerfen?")) {
                  return;
                }
                if (next !== tab) setDirty(false);
                setTab(next);
              }}
              options={tabOptions}
              className="min-w-max"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {canFill && activeSheet ? (
              <Button type="button" size="sm" variant="secondary" disabled={saving || !dirty} onClick={() => void handleSave()}>
                {saving ? "Speichert …" : "Speichern"}
              </Button>
            ) : null}
            <Button type="button" size="sm" variant="secondary" onClick={() => void handlePrint()}>
              Drucken
            </Button>
            {canFill ? (
              <Popover className="relative">
                {({ close }) => (
                  <>
                    <PopoverButton
                      type="button"
                      className="inline-flex h-8 items-center justify-center rounded-lg bg-accent px-3 text-sm font-medium text-label-on-accent shadow-sm transition hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft"
                    >
                      Hinzufügen
                    </PopoverButton>
                    <PopoverPanel className="absolute right-0 z-[320] mt-1 w-56 rounded-ios border border-separator bg-card p-1 shadow-ios-md">
                      <div className="flex flex-col">
                        <button
                          type="button"
                          className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary"
                          onClick={() => {
                            close();
                            void handleCreateSheet();
                          }}
                        >
                          Brandschutztür
                        </button>
                        {canManage ? (
                          <>
                            <button type="button" className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary" onClick={() => { setEditingItem(null); setAddMode("file"); close(); }}>
                              {isHandheld ? "Aus Mediathek" : "Datei vom PC"}
                            </button>
                            {isHandheld ? null : (
                              <button type="button" className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary" onClick={() => { setEditingItem(null); setAddMode("mobile"); close(); }}>
                                Vom Handy (QR)
                              </button>
                            )}
                            <button type="button" className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary" onClick={() => { setEditingItem(null); setAddMode("project_fotos"); close(); }}>
                              Projektfotos
                            </button>
                            {isBuero ? (
                              <button type="button" className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary" onClick={() => { setEditingItem(null); setAddMode("sharepoint"); close(); }}>
                                Aus SharePoint
                              </button>
                            ) : null}
                            <button type="button" className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary" onClick={() => { setEditingItem(null); setAddMode("note"); close(); }}>
                              Notiz
                            </button>
                            <button type="button" className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary" onClick={() => { setEditingItem(null); setAddMode("address"); close(); }}>
                              Adresse
                            </button>
                          </>
                        ) : null}
                      </div>
                    </PopoverPanel>
                  </>
                )}
              </Popover>
            ) : null}
          </div>
        </div>

        {error ? <Alert variant="error" className="mb-3">{error}</Alert> : null}
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : null}

        {!loading && tab === "deckblatt" ? (
          <MontagePdfEmbed
            loadKey={`aufmass-deckblatt-${orderId}`}
            title="Aufmaß-Deckblatt"
            loadPdf={() => fetchAufmassDeckblattPdf(orderId)}
            paperKey={`aufmass-deckblatt-${orderId}`}
          />
        ) : null}

        {!loading && activeSheet ? (
          <div className="space-y-3">
            <BrandschutzBlattForm
              schema={schema}
              felder={draft}
              auftraggeber={auftraggeber}
              auftragsnummer={auftragsnummer}
              disabled={!canFill}
              onChange={patchField}
            />
            {canManage ? (
              <Button type="button" size="sm" variant="secondary" onClick={() => void handleDeleteSheet(activeSheet)}>
                Aufmaßblatt löschen
              </Button>
            ) : null}
          </div>
        ) : null}

        {!loading && selectedFile ? <AufmassFilePreview orderId={orderId} item={selectedFile} /> : null}

        {!loading && tab === "dateien" ? (
          <div className="space-y-4">
            {files.length + notes.length === 0 ? (
              <p className="text-sm text-label-secondary">Noch keine Dateien, Notizen oder Adressen.</p>
            ) : (
              <ul className="space-y-2">
                {[...notes, ...files].map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-2 rounded-lg border border-separator px-3 py-2">
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left text-sm"
                      onClick={() => {
                        if (item.entry_type === "file") {
                          setTab(`file-${item.id}`);
                          return;
                        }
                        if (item.entry_type === "note" || item.entry_type === "address") {
                          setEditingItem(item);
                          setAddMode(item.entry_type);
                        }
                      }}
                    >
                      <span className="block font-medium">{fileLabel(item)}</span>
                      {item.entry_type !== "file" && item.body_text ? (
                        <span className="block truncate text-xs text-label-secondary">{item.body_text}</span>
                      ) : null}
                    </button>
                    {canManage ? (
                      <Button type="button" size="sm" variant="secondary" onClick={() => void handleDelete(item)}>
                        Löschen
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </Dialog>

      <MontageunterlagenAddDialog
        open={addMode != null}
        mode={addMode}
        orderId={orderId}
        pack="aufmass"
        user={user}
        groups={groups}
        editingItem={editingItem}
        onClose={() => {
          setAddMode(null);
          setEditingItem(null);
        }}
        onChanged={() => {
          void getAufmassunterlagen(orderId).then((files) => {
            setItems(Array.isArray(files) ? files : []);
          });
        }}
      />
      <MontageunterlagenPrintDialog
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        orderId={orderId}
        tabs={printTabs}
        pack="aufmass"
      />
    </>
  );
}
